import type { ReactElement } from 'react';

import { useRouter } from 'next/compat/router';
import Head from 'next/head';
import { useMemo } from 'react';
import { useStats } from 'react-instantsearch';

import { useAlgoliaLoadingState } from '../../../../utilities/hooks/search/useAlgoliaLoadingState';
import { useAppContext } from '~/utilities/context/static/AppContext';

export const ProductListDynamicMetaHead = (): ReactElement => {
  const { locale } = useAppContext();
  const router = useRouter();
  const asPath = router?.asPath ?? '';
  const { page, nbPages } = useStats();
  const { isFirstLoading } = useAlgoliaLoadingState();

  const { prevLink, nextLink, currentLink } = useMemo(() => {
    const params = asPath.replace(/^[^?]*/, '');
    const { protocol, host } = window.location;
    const cleanPath = `${protocol}//${host}/${locale}${asPath.replace(/\?(.*)/, '')}`;

    const getCurrentLink = () => {
      const searchParams = new URLSearchParams(params);

      searchParams.set('page', `${page + 1}`);

      return page !== 0 ? `${cleanPath}?${searchParams.toString()}` : cleanPath;
    };

    const getPrevLinkParams = () => {
      const searchParams = new URLSearchParams(params);

      if (page === 1) {
        searchParams.delete('page');
      } else {
        searchParams.set('page', `${page}`);
      }

      return searchParams.toString();
    };

    const getNextLinkParams = () => {
      const searchParams = new URLSearchParams(params);

      searchParams.set('page', `${page === 0 ? 2 : page + 2}`);

      return searchParams.toString();
    };

    return {
      currentLink: getCurrentLink(),
      prevLink: `${cleanPath}?${getPrevLinkParams()}`,
      nextLink: `${cleanPath}?${getNextLinkParams()}`,
    };
  }, [page, asPath, locale]);

  return isFirstLoading ? (
    <></>
  ) : (
    <Head>
      <meta property="og:url" content={currentLink} />
      <link rel="canonical" href={currentLink} />

      {page !== 0 && <link rel="prev" href={prevLink} />}
      {page !== nbPages - 1 && <link rel="next" href={nextLink} />}
    </Head>
  );
};
